
import { supabase } from '@/lib/supabase/client';
import { generateAuthUrl, YOUTUBE_CLIENT_ID, YOUTUBE_CLIENT_SECRET, YOUTUBE_TOKEN_URL } from './config';
import { YouTubeChannelListResponse, YouTubeChannelItem } from './types';
import { YouTubeChannel } from '@/components/dashboard/profiles/types';

// Fonction pour obtenir un token d'accès YouTube à partir du code d'autorisation
export const getAccessToken = async (code: string, state: string) => {
  try {
    const response = await fetch(YOUTUBE_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
      body: new URLSearchParams({
        code,
        client_id: YOUTUBE_CLIENT_ID,
        client_secret: YOUTUBE_CLIENT_SECRET,
        redirect_uri: new URL('/youtube-auth-callback', window.location.origin).toString(),
        grant_type: 'authorization_code',
      }),
    });

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Erreur lors de l\'obtention du token:', errorData);
      throw new Error(`Erreur lors de l'obtention du token: ${errorData.error}`);
    }

    const data = await response.json();
    return {
      access_token: data.access_token,
      refresh_token: data.refresh_token,
      expires_in: data.expires_in,
    };
  } catch (error) {
    console.error('Exception lors de l\'obtention du token:', error);
    throw error;
  }
};

// Fonction pour récupérer les chaînes YouTube de l'utilisateur
export const fetchYouTubeChannels = async (accessToken: string): Promise<YouTubeChannel[]> => {
  try {
    // Récupérer les chaînes de l'utilisateur connecté
    const response = await fetch(
      'https://www.googleapis.com/youtube/v3/channels?part=snippet,statistics&mine=true',
      {
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'application/json',
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('Erreur lors de la récupération des chaînes:', errorData);
      throw new Error(`Erreur lors de la récupération des chaînes: ${errorData.error?.message || 'Erreur inconnue'}`);
    }

    const data: YouTubeChannelListResponse = await response.json();
    console.log('Chaînes YouTube récupérées:', data);

    // Transformer les données pour correspondre à notre interface YouTubeChannel
    return data.items.map((item: YouTubeChannelItem) => ({
      id: item.id,
      title: item.snippet.title,
      description: item.snippet.description,
      thumbnailUrl: item.snippet.thumbnails.default.url,
      subscriberCount: parseInt(item.statistics.subscriberCount),
      videoCount: parseInt(item.statistics.videoCount),
    }));
  } catch (error) {
    console.error('Exception lors de la récupération des chaînes:', error);
    throw error;
  }
};

// Fonction pour sauvegarder un profil YouTube dans Supabase
export const saveYouTubeProfile = async (
  channelId: string,
  channelTitle: string,
  channelDescription: string,
  channelThumbnailUrl: string,
  accessToken: string,
  refreshToken: string,
  tokenExpiry: number,
  subscriberCount: number = 0,
  videoCount: number = 0
) => {
  try {
    const organizationId = localStorage.getItem('currentOrganizationId');
    if (!organizationId) {
      throw new Error('ID d\'organisation non trouvé');
    }

    const { data: user } = await supabase.auth.getUser();
    if (!user || !user.user) {
      throw new Error('Utilisateur non authentifié');
    }

    const { data, error } = await supabase
      .from('social_profiles')
      .insert([
        {
          name: channelTitle,
          platform: 'youtube',
          profile_url: `https://www.youtube.com/channel/${channelId}`,
          organization_id: organizationId,
          created_by: user.user.id,
          youtube_channel_id: channelId,
          youtube_access_token: accessToken,
          youtube_refresh_token: refreshToken,
          youtube_token_expiry: tokenExpiry,
          account_id: channelId,
          account_name: channelTitle,
          account_description: channelDescription,
          account_avatar: channelThumbnailUrl,
          follower_count: subscriberCount,
          post_count: videoCount,
        },
      ])
      .select('*')
      .single();

    if (error) {
      console.error('Erreur lors de l\'enregistrement du profil YouTube:', error);
      throw error;
    }

    return data; // Retourner les données du profil créé pour pouvoir utiliser l'ID
  } catch (error) {
    console.error('Exception lors de l\'enregistrement du profil YouTube:', error);
    throw error;
  }
};
